<template>
  <div
    v-for="{ page, pageIndex } in visiblePages"
    :key="pageIndex"
    v-element-visibility="(isVisible: boolean) => onPageVisibility(isVisible, page, pageIndex)"
    class="page"
    :class="{
      'flex flex-col flex-auto overflow-hidden': isScaleContentEnabled,
    }"
    :style="pageStyle"
  >
    <PageWrapperPaginated
      :aspect-ratio="aspectRatio"
      :aspect-ratio-style="aspectRatioStyle"
      :is-scale-content-enabled="isScaleContentEnabled"
    >
      <PaginatedContent
        :component-map="componentMap"
        :get-child-component-style="getChildComponentStyle"
        :get-component-bindings="getComponentBindings"
        :get-top-level-component-style="getTopLevelComponentStyle"
        :on-element-visibility="onElementVisibility"
        :page="page"
        :should-render-component-wrapper="shouldRenderComponentWrapper"
      />
    </PageWrapperPaginated>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, type ComputedRef, type CSSProperties, ref } from 'vue'
import { vElementVisibility } from '@vueuse/components'
import { useSwipe } from '@vueuse/core'
import { omit } from 'lodash-es'

import Block from '@canvas-builder/components/ui/Block/Block.vue'
import Carousel from '@canvas-builder/components/ui/Carousel/Carousel.vue'
import CollectionPlayer from '@canvas-builder/components/ui/CollectionPlayer/CollectionPlayer.vue'
import ContentGrid from '@canvas-builder/components/ui/ContentGrid/ContentGrid.vue'
import CustomDataTable from '@canvas-builder/components/ui/CustomDataTable/CustomDataTable.vue'
import DataCharts from '@canvas-builder/components/ui/DataCharts/DataCharts.vue'
import DynamicDataTable from '@canvas-builder/components/ui/DynamicDataTable/DynamicDataTable.vue'
import Embeddable from '@canvas-builder/components/ui/Embeddable/Embeddable.vue'
import Grid from '@canvas-builder/components/ui/Grid/Grid.vue'
import Html from '@canvas-builder/components/ui/Html/Html.vue'
import IFrame from '@canvas-builder/components/ui/IFrame/IFrame.vue'
import Link from '@canvas-builder/components/ui/Link/Link.vue'
import Multimedia from '@canvas-builder/components/ui/Multimedia/Multimedia.vue'
import PageBreak from '@canvas-builder/components/ui/PageBreak/PageBreak.vue'
import Scribble from '@canvas-builder/components/ui/Scribble/Scribble.vue'
import SectionList from '@canvas-builder/components/ui/SectionList/SectionList.vue'
import Text from '@canvas-builder/components/ui/Text/Text.vue'
import Timeline from '@canvas-builder/components/ui/Timeline/Timeline.vue'
import PageWrapperPaginated from './pageWrapper.paginated.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasVisibility from '@canvas-builder/composables/useCanvasVisibility'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import useEmbeddable from '@canvas-builder/composables/useEmbeddable'
import type { SectionListProps } from '@canvas-builder/types/canvas'
import { ComponentTypes, type ComponentNode, type ComponentTrackingParams } from '@canvas-builder/types/canvas'
import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types.ts'
import type { InstanceSettings } from '@lib/types/instanceSettings.types.ts'
import PaginatedContent from '@canvas-builder/components/container/CanvasContent/page.paginated.vue'
import { convertToPixels } from '@canvas-builder/util/print.util'
import ContentList from '@canvas-builder/components/ui/ContentList/ContentList.vue'

const componentMap = {
  Block,
  Carousel,
  CollectionPlayer,
  ContentGrid,
  CustomDataTable,
  DataCharts,
  DynamicDataTable,
  Embeddable,
  Grid,
  Html,
  IFrame,
  Link,
  Multimedia,
  PageBreak,
  Scribble,
  SectionList,
  ContentList,
  Text,
  Timeline,
} as Record<ComponentTypes, any>

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const { globalComponentSpacing } = useCanvasTheme()
const {
  isEditMode,
  onComponentEnter,
  onComponentExit,
  onPageEnter,
  onPageExit,
  contentRef,
  displayMode,
  activateNavigationItem,
  deactivateNavigationItem,
  isPrintDisplayMode,
  activeCanvas,
  contentAnnotation,
} = useCanvas()

const createdInEditMode = isEditMode.value
const { shouldRenderComponentWrapper } = useCanvasVisibility()
const { canvasPages, currentPageIndex, goToNextPage, goToPreviousPage, keepActivePageValid } = useCanvasPages()
const { hasZeroHeight } = useEmbeddable()
keepActivePageValid()

const isPresentationWithNoCollapsibles = computed(
  () => (pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') !== 'collapsibles' && !isEditMode.value,
)

const visiblePages = computed<{ page: ComponentNode[]; pageIndex: number }[]>(() => {
  if (displayMode.value === 'print') {
    return canvasPages.value.reduce<{ page: ComponentNode[]; pageIndex: number }[]>((acc, page, pageIndex) => {
      const sectionListIndex = page.findIndex((child) => child.type === ComponentTypes.SectionList)

      if (sectionListIndex === -1) {
        acc.push({ page, pageIndex })
        return acc
      }

      const sectionList = page[sectionListIndex] as ComponentNode<SectionListProps>

      if (!Array.isArray(sectionList?.data?.sections) || sectionList?.data?.sections.length === 0) {
        acc.push({ page, pageIndex })
        return acc
      }

      // Distribute each section to separate SectionList
      sectionList?.data?.sections.forEach((section, sectionIndex) => {
        const newPage = [{ ...sectionList, data: { ...sectionList.data, sections: [section] }, sectionIndex }]

        acc.push({ page: newPage, pageIndex })
      })

      return acc
    }, [])
  }

  return [{ page: canvasPages.value[currentPageIndex.value] ?? [], pageIndex: currentPageIndex.value }]
})

const aspectRatio = computed(() => {
  const rawRatio: string | undefined = activeCanvas.value?.theme?.aspect_ratio

  if (!rawRatio || rawRatio === 'contain' || rawRatio === 'unlocked') return undefined

  const [x, y] = rawRatio.split(':').map((n) => parseInt(n))

  if (isNaN(x) || isNaN(y)) return undefined

  return {
    x,
    y,
  }
})

const aspectRatioStyle = computed(() => {
  if (!aspectRatio.value || isPrintDisplayMode.value) return undefined

  const { x, y } = aspectRatio.value

  return {
    aspectRatio: `${x} / ${y}`,
  }
})

const pageStyle = computed(() => {
  if (!isPrintDisplayMode.value || !launchDarkly.value.enable_scale_content) {
    return undefined
  }

  // Use canvas-specific PDF dimensions if set, otherwise fall back to pitcher settings default
  const canvasPdfWidth =
    launchDarkly.value.enable_canvas_pdf_dimensions && activeCanvas.value?.theme?.pdf_dimensions?.width
  const canvasPdfHeight =
    launchDarkly.value.enable_canvas_pdf_dimensions && activeCanvas.value?.theme?.pdf_dimensions?.height

  const pdfWidth = canvasPdfWidth || pitcherSettings.value.default_pdf_page_dimensions?.width
  const pdfHeight = canvasPdfHeight || pitcherSettings.value.default_pdf_page_dimensions?.height

  if (!pdfWidth || !pdfHeight) {
    return undefined
  }

  const widthPx = convertToPixels(pdfWidth)
  const heightPx = convertToPixels(pdfHeight)

  if (widthPx === undefined || heightPx === undefined || widthPx <= 0 || heightPx <= 0) {
    return undefined
  }

  return {
    width: `${widthPx}px`,
    height: `${heightPx}px`,
  }
})

defineProps<{
  isScaleContentEnabled?: boolean
}>()

useSwipe(
  computed(() => contentRef.value?.$el),
  {
    onSwipeEnd: (e, direction) => {
      const isTouchInsideSwiper = !!(e.target as HTMLElement)?.closest('swiper-container')
      const isActiveAnnotationContentDrawing =
        contentAnnotation.value?.isDrawingActive &&
        !isEditMode.value &&
        pitcherSettings.value?.enable_presentation_annotations

      if (
        isEditMode.value ||
        !isPresentationWithNoCollapsibles.value ||
        isTouchInsideSwiper ||
        isActiveAnnotationContentDrawing
      )
        return
      if (direction === 'left') {
        goToNextPage()
      } else if (direction === 'right') {
        goToPreviousPage()
      }
    },
  },
)

const activePages = ref<Record<number, boolean>>({})

const onPageVisibility = (isVisible: boolean, page: ComponentNode[], pageIndex: number) => {
  if (isVisible) {
    activePages.value[pageIndex] = true
    onPageEnter.value?.(page, pageIndex)
  } else if (activePages.value[pageIndex]) {
    delete activePages.value[pageIndex]
    onPageExit.value?.(page, pageIndex)
  }
}

const onElementVisibility = (isVisible: boolean, c: ComponentNode) => {
  const params: ComponentTrackingParams = { trackable: true }
  if (isVisible) {
    onComponentEnter.value?.(c, params)

    if ([ComponentTypes.SectionList, ComponentTypes.ContentList].includes(c.type)) return

    activateNavigationItem(c.id, undefined, !createdInEditMode)
  } else {
    onComponentExit.value?.(c, params)

    if ([ComponentTypes.SectionList, ComponentTypes.ContentList].includes(c.type)) return

    deactivateNavigationItem(c.id, undefined, !createdInEditMode)
  }
}

function getComponentBindings(component: ComponentNode) {
  const baseBindings =
    component.type === ComponentTypes.Grid || component.type === ComponentTypes.Scribble
      ? {
          ...omit(component, 'type'),
          style: {
            ...component?.style,
            marginBottom: undefined,
          } satisfies CSSProperties,
        }
      : {
          ...omit(component, 'children', 'type'),
          style: {
            ...component?.style,
            marginBottom: undefined,
          } satisfies CSSProperties,
        }

  // Pass sectionIndex as printModeIdx for SectionList components in print mode
  if (component.type === ComponentTypes.SectionList && 'sectionIndex' in component) {
    return {
      ...baseBindings,
      printModeIdx: (component as any).sectionIndex,
    }
  }

  return baseBindings
}

function getComponentStyleWithSpacing(
  shouldRemoveMargin: boolean,
  shouldRemoveMarginLegacy: boolean,
  hasScribbleException: boolean,
  component: ComponentNode,
  nextComponent?: ComponentNode,
): CSSProperties {
  const hasIntuitiveSpacing = launchDarkly.value.enable_more_intuitive_component_spacing

  if (hasIntuitiveSpacing) {
    const nextEmbeddableHasZeroHeight =
      nextComponent?.type === ComponentTypes.Embeddable && nextComponent.id && hasZeroHeight(nextComponent.id)

    return shouldRemoveMargin || nextEmbeddableHasZeroHeight
      ? {}
      : {
          marginBottom: component?.style?.marginBottom || `${globalComponentSpacing.value}px`,
        }
  } else {
    // Legacy behavior with print mode and scribble exceptions
    if (shouldRemoveMarginLegacy || hasScribbleException) {
      return {}
    }
    return {
      marginBottom: component?.style?.marginBottom || `${globalComponentSpacing.value}px`,
    }
  }
}

function getChildComponentStyle(
  parentComponent: ComponentNode,
  childComponent: ComponentNode,
  childIndex: number,
): CSSProperties {
  const childrenLength = parentComponent.children?.length ?? 0
  const isLastChild = childrenLength > 0 && childIndex === childrenLength - 1
  const isOnlyChild = childrenLength === 1

  const hasScribbleException =
    !isPrintDisplayMode.value &&
    !!launchDarkly.value.enable_responsive_scribble_component &&
    childComponent.type === ComponentTypes.Scribble

  const nextChild = childIndex < childrenLength - 1 ? parentComponent.children?.[childIndex + 1] : undefined

  return getComponentStyleWithSpacing(
    isLastChild || isOnlyChild,
    isPrintDisplayMode.value && isLastChild,
    hasScribbleException,
    childComponent,
    nextChild,
  )
}

function getTopLevelComponentStyle(
  page: ComponentNode[],
  component: ComponentNode,
  componentIndex: number,
): CSSProperties {
  const filteredComponents = page.filter((component) => shouldRenderComponentWrapper(component))
  const isLastComponent = componentIndex === filteredComponents.length - 1
  const isOnlyComponent = filteredComponents.length === 1

  const hasScribbleException =
    !isPrintDisplayMode.value &&
    !!launchDarkly.value.enable_responsive_scribble_component &&
    component.type === ComponentTypes.Scribble

  const nextComponent =
    componentIndex < filteredComponents.length - 1 ? filteredComponents[componentIndex + 1] : undefined

  return getComponentStyleWithSpacing(
    isLastComponent || isOnlyComponent,
    isPrintDisplayMode.value && isLastComponent,
    hasScribbleException,
    component,
    nextComponent,
  )
}
</script>

<style lang="scss" scoped>
@media print {
  .page {
    &:not(:last-child) {
      break-after: page;
    }
  }
}
</style>
